import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

import {
  selectFiltersAssociationByProfile,
  selectProcessingFilters,
} from 'app/redux/processing/reducer';
import {
  DEFAULT_PROCESSING_ASSOCIATION_BY_SPECIE_AND_REGION,
  ProcessingFiltersAssociationBySpecieAndRegion,
} from 'app/utils/PROCESSING_OPTIONS';
import _ from 'lodash';

const LEAF_IDENTIFIERS = [
  'undefined',
  'boolean',
  'number',
  'bigint',
  'string',
  'symbol',
  'function',
];

function deepKeepBy<T>(obj: T, predicate: (value: any) => boolean): T | undefined {
  function shouldKeep(value: any): boolean {
    if (LEAF_IDENTIFIERS.includes(typeof value) || Array.isArray(value)) {
      return predicate(value);
    }
    return true;
  }
  if (LEAF_IDENTIFIERS.includes(typeof obj) || Array.isArray(obj)) {
    if (predicate(obj)) {
      return obj;
    } else {
      return undefined;
    }
  }

  return _.mapValues(_.pickBy(obj as any, shouldKeep), (value) =>
    deepKeepBy(value, predicate)
  ) as T;
}

export default function useFiltersAssociationByProfileWithDefaults(): ProcessingFiltersAssociationBySpecieAndRegion {
  const filtersAssociationByProfile = useSelector(selectFiltersAssociationByProfile);
  const processingFilters = useSelector(selectProcessingFilters);
  // We ensure that configured processing filters really exist in the store
  const existingFiltersAssociationByProfile: ProcessingFiltersAssociationBySpecieAndRegion =
    useMemo(() => {
      return deepKeepBy(filtersAssociationByProfile, (value) => {
        return _.some(processingFilters, { id: value });
      });
    }, [filtersAssociationByProfile, processingFilters]);

  return React.useMemo(() => {
    return _.merge(
      {},
      DEFAULT_PROCESSING_ASSOCIATION_BY_SPECIE_AND_REGION,
      existingFiltersAssociationByProfile
    );
  }, [existingFiltersAssociationByProfile]);
}
